import "../models/contract-rich-text.css";
import DOMPurify from "dompurify";

const ALLOWED_TAGS = new Set([
  "A",
  "B",
  "BLOCKQUOTE",
  "BR",
  "CAPTION",
  "COL",
  "COLGROUP",
  "DD",
  "DIV",
  "DL",
  "DT",
  "EM",
  "FIGCAPTION",
  "FIGURE",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HR",
  "I",
  "LI",
  "OL",
  "P",
  "PRE",
  "S",
  "SECTION",
  "SMALL",
  "SPAN",
  "STRONG",
  "SUB",
  "SUP",
  "TABLE",
  "TBODY",
  "TD",
  "TFOOT",
  "TH",
  "THEAD",
  "TR",
  "U",
  "UL",
]);

const ALLOWED_ATTRIBUTES = new Set([
  "align",
  "class",
  "colspan",
  "dir",
  "href",
  "lang",
  "rowspan",
  "start",
  "target",
  "title",
  "type",
  "data-list",
]);


export function isSafeContractHref(value: string): boolean {
  const href = value.trim();
  if (href.includes("\\") || [...href].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127)) return false;
  return /^(https?:|mailto:|tel:|#|\/(?!\/))/i.test(href);
}

/** Sanitize at render/edit boundaries without rewriting stored contracts. */
export function sanitizeContractHtml(html: string): string {
  const root = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [...ALLOWED_TAGS].map(tag => tag.toLowerCase()),
    ALLOWED_ATTR: [...ALLOWED_ATTRIBUTES],
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
    RETURN_DOM: true,
  }) as HTMLElement;
  for (const element of root.querySelectorAll("a")) {
    const href = element.getAttribute("href");
    if (href && !isSafeContractHref(href)) element.removeAttribute("href");
    if (element.getAttribute("target") === "_blank") element.setAttribute("rel", "noopener noreferrer");
  }
  return root.innerHTML;
}

/** Convert legacy Quill list metadata on a rendering copy, never the stored value. */
export function renderContractHtml(html: string): string {
  const parsed = new DOMParser().parseFromString(sanitizeContractHtml(html), "text/html");
  parsed.querySelectorAll(".ql-ui").forEach(node => node.remove());
  // HTML type selectors are case-insensitive; derive case-sensitive marker classes.
  const listStyles: Record<string, string> = { A: "upper-alpha", a: "lower-alpha", I: "upper-roman", i: "lower-roman" };
  for (const list of parsed.body.querySelectorAll("ol[type]")) {
    for (const kind of Object.values(listStyles)) list.classList.remove(`contract-list-${kind}`);
    const kind = listStyles[list.getAttribute("type") || ""];
    if (kind) list.classList.add(`contract-list-${kind}`);
  }
  for (const original of Array.from(parsed.body.querySelectorAll("ol,ul"))) {
    const items = Array.from(original.children).filter((node): node is HTMLLIElement => node instanceof HTMLLIElement);
    if (!items.some(item => item.hasAttribute("data-list") || /(?:^|\s)ql-indent-\d/.test(item.className))) continue;
    const fragment = parsed.createDocumentFragment();
    const levels: { list: HTMLElement; item: HTMLLIElement | null }[] = [];
    for (const item of items) {
      const indent = Number(item.className.match(/(?:^|\s)ql-indent-(\d+)/)?.[1] || 0);
      let depth = Math.min(indent, levels.length, 8);
      if (depth > 0 && !levels[depth - 1]?.item) depth = 0;
      levels.length = Math.min(levels.length, depth + 1);
      const kind = item.getAttribute("data-list") === "bullet" ? "UL" : item.getAttribute("data-list") === "ordered" ? "OL" : original.tagName;
      if (!levels[depth] || levels[depth].list.tagName !== kind) {
        const list = parsed.createElement(kind.toLowerCase());
        if (depth === 0) for (const attribute of Array.from(original.attributes)) list.setAttribute(attribute.name, attribute.value);
        if (depth === 0) fragment.appendChild(list);
        else levels[depth - 1].item?.appendChild(list);
        levels[depth] = { list, item: null };
      }
      item.className = item.className.replace(/(?:^|\s)ql-indent-\d+/g, "").trim();
      if (!item.className) item.removeAttribute("class");
      item.removeAttribute("data-list");
      levels[depth].list.appendChild(item);
      levels[depth].item = item;
    }
    original.replaceWith(fragment);
  }
  return parsed.body.innerHTML;
}

/** Keep safe original formatting bytes when the editor has not changed the document. */
export function preserveSafeContractHtml(html: string): string {
  const sanitized = sanitizeContractHtml(html);
  const original = new DOMParser().parseFromString(html, "text/html");
  return original.head.innerHTML === "" && original.body.innerHTML === sanitized ? html : sanitized;
}
